import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { Flex, Grid, Text } from '@backstage/ui';
import useAsync from 'react-use/esm/useAsync';
import { PersonMiniCard } from './PersonMiniCard';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { HomeCard } from './HomeCard';

const START_DATE_ANNOTATION = 'datamarx.com/start-date';
const LOOKBACK_DAYS = 365;

function parseDateUTC(dateStr: string): Date {
  return new Date(`${dateStr}T12:00:00Z`);
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function tenureLabel(startDate: Date): string {
  const days = Math.floor((Date.now() - startDate.getTime()) / 86_400_000);
  if (days < 90) {
    return days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`;
  }
  const years = (Date.now() - startDate.getTime()) / (365.25 * 86_400_000);
  if (years < 1) return '< 1 year';
  const rounded = Math.floor(years);
  return `${rounded} year${rounded === 1 ? '' : 's'}`;
}

export const NewHiresCard = () => {
  const catalogApi = useApi(catalogApiRef);

  const { value, loading, error } = useAsync(async () => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - LOOKBACK_DAYS);
    cutoff.setHours(0, 0, 0, 0);

    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'User',
        [`metadata.annotations.${START_DATE_ANNOTATION}`]:
          CATALOG_FILTER_EXISTS,
      },
      fields: [
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        `metadata.annotations.${START_DATE_ANNOTATION}`,
        'spec.profile',
      ],
    });

    return items
      .flatMap(entity => {
        const dateStr = entity.metadata.annotations?.[START_DATE_ANNOTATION];
        if (!dateStr) return [];
        const startDate = parseDateUTC(dateStr);
        if (startDate < cutoff) return [];
        return [{ entity, startDate }];
      })
      .sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
      .slice(0, 10);
  }, [catalogApi]);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const hires = value ?? [];

  // Card chrome and heading come from the home page widget wrapper.
  return (
    <Flex direction="column" gap="3">
      {hires.length === 0 ? (
        <Text variant="body-small" color="secondary">
          No new hires in the last year.
        </Text>
      ) : (
        <Grid.Root className="primary-home-people-grid" gap="2">
          {hires.map(({ entity, startDate }) => (
            <PersonMiniCard
              key={entity.metadata.name}
              entity={entity}
              line1={formatDate(startDate)}
              line2={tenureLabel(startDate)}
            />
          ))}
        </Grid.Root>
      )}
    </Flex>
  );
};

export const newHiresWidget = HomePageWidgetBlueprint.make({
  name: 'new-hires',
  params: {
    name: 'NewHires',
    title: '🎉 New Hires',
    description: 'People who joined DataMarx in the last year',
    components: () =>
      import('./NewHiresCard').then(m => ({ Content: m.NewHiresCard })),
    componentProps: { Renderer: HomeCard },
    layout: {
      width: { defaultColumns: 6, minColumns: 3 },
      height: { defaultRows: 7, minRows: 3 },
    },
  },
});
